import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FormFieldComponent from '@/components/FormFieldComponent';
import Loader from '@/components/Loader';
import { FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { useGetWorkOrderById } from '@/services/workOrderService';
import {
  confirmProcessBillingVendor,
  getProcessBillingVendor,
  patchProcessBillingVendor,
  postProcessBillingVendor,
} from '@/services/processBillingVendorService';
import { uploadMedia } from '@/services/uploadMedia';
import {
  affidavitNameForSlot,
  getVendorPrefillForBilling,
  isProcessBillingVendorEligible,
} from '@/pages/caseManagement/processBilling/processBillingVendorUtils';
import type {
  ProcessBillingVendorGetResponse,
  ProcessBillingVendorPatchRequest,
  ProcessBillingVendorPostRequest,
} from '@/types/processBillingVendor.types';
import type { WorkOrderGetByIdResponse } from '@/types/workOrder.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useUserStore } from '@/stores/user';
import { canAccessProcessBilling, isAdminOrSupervisor as isAdminOrSupervisorUser } from '@/services/userService';
import { toast } from '@/components/ui/use-toast';
import { Feature } from '@/utils/constant';
import { hasFeature } from '@/services/featureService';

const MAX_PDF_BYTES = 20 * 1024 * 1024;
const VENDOR_INVOICE_PATTERN = /^[A-Za-z0-9_-]+$/;
const VENDOR_SERVER_ID_PATTERN = /^[A-Za-z0-9-]+$/;

const PROOF_SECTION_SUBTITLE =
  'Includes affidavits and invoices shared by vendor. PDF only, max 20 MB per file. At least one file is required.';

type DocFormRow = {
  clientKey: string;
  name: string;
  mediaId: string;
  processBillingVendorDocumentId?: string;
};

type NullableString = string | null;

type VendorBillingFormValues = {
  vendorName: string;
  vendorContactName: string;
  vendorServerName: NullableString;
  serverId: NullableString;
  vendorInvoiceNumber: NullableString;
  amountDue: NullableString;
  documents: DocFormRow[];
};

const trimToString = (v: NullableString | undefined): string => (typeof v === 'string' ? v.trim() : '');

function parseMoney(label: string, raw: string | null | undefined): number {
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (!text) {
    throw new Error(`${label} is required`);
  }
  const n = Number(text);
  if (!Number.isFinite(n) || n < 0) {
    throw new Error(`${label} must be a number ≥ 0`);
  }
  const rounded = Math.round(n * 100) / 100;
  if (Math.abs(rounded - n) > 1e-6) {
    throw new Error(`${label} may have at most 2 decimal places`);
  }
  return rounded;
}

function buildCreateDefaults(wo: WorkOrderGetByIdResponse): VendorBillingFormValues {
  const prefill = getVendorPrefillForBilling(wo);
  return {
    vendorName: prefill?.vendorName ?? '',
    vendorContactName: prefill?.vendorContactName ?? '',
    vendorServerName: '',
    serverId: '',
    vendorInvoiceNumber: '',
    amountDue: '',
    documents: [{ clientKey: crypto.randomUUID(), name: '', mediaId: '' }],
  };
}

function buildEditDefaults(
  wo: WorkOrderGetByIdResponse,
  billing: ProcessBillingVendorGetResponse
): VendorBillingFormValues {
  const prefill = getVendorPrefillForBilling(wo);
  const entry = billing.entry;
  if (!entry) return buildCreateDefaults(wo);
  const pickNonEmpty = (...values: (string | null | undefined)[]): string => {
    for (const v of values) if (v?.trim()) return v;
    return '';
  };
  return {
    vendorName: pickNonEmpty(entry.vendorName, prefill?.vendorName),
    vendorContactName: pickNonEmpty(entry.vendorContactName, prefill?.vendorContactName),
    vendorServerName: entry.vendorServerName ?? '',
    serverId: entry.serverId ?? '',
    vendorInvoiceNumber: entry.vendorInvoiceNumber,
    amountDue: String(entry.amountDue),
    documents: entry.proofOfService.map((d) => ({
      clientKey: crypto.randomUUID(),
      name: d.name,
      mediaId: d.mediaId,
      processBillingVendorDocumentId: d.processBillingVendorDocumentId,
    })),
  };
}

function VendorDocumentsSection({ filesRef }: { filesRef: MutableRefObject<Record<string, File | undefined>> }) {
  const { control, register, watch, getValues, setValue } = useFormContext<VendorBillingFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'documents',
  });

  useEffect(() => {
    fields.forEach((_field, docIdx) => {
      const current = getValues(`documents.${docIdx}.clientKey`);
      if (typeof current !== 'string' || current.length === 0) {
        setValue(`documents.${docIdx}.clientKey`, crypto.randomUUID(), {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    });
  }, [fields, getValues, setValue]);

  const docClientKey = (docIdx: number): string => {
    const fromForm = getValues(`documents.${docIdx}.clientKey`);
    if (typeof fromForm === 'string' && fromForm.length > 0) return fromForm;
    const w = watch(`documents.${docIdx}.clientKey`);
    if (typeof w === 'string' && w.length > 0) return w;
    const fallback = fields[docIdx]?.id ?? '';
    if (fallback) {
      setValue(`documents.${docIdx}.clientKey`, fallback, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
    return fallback;
  };

  const [, forceRerender] = useState(0);

  return (
    <div className="rounded-lg bg-[#F5F5F5] p-5 w-full">
      <div className="flex flex-row justify-between items-start mb-4 gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-base font-semibold text-primary">
            Proof of Service<span className="text-error align-top ml-0.5">*</span>
          </div>
          <p className="text-sm text-placeholder mt-1">{PROOF_SECTION_SUBTITLE}</p>
        </div>
        <button
          type="button"
          aria-label="Add document"
          className="bg-white border border-bordercolor rounded-md p-1.5 text-primary hover:bg-[#EDF5FF]"
          onClick={() => append({ clientKey: crypto.randomUUID(), name: '', mediaId: '' })}
        >
          <Plus className="h-4 w-4 text-primary" />
        </button>
      </div>
      <div className="flex flex-col gap-3">
        {fields.map((field, docIdx) => {
          const mediaId = watch(`documents.${docIdx}.mediaId`);
          const docName = watch(`documents.${docIdx}.name`);
          const ck = docClientKey(docIdx);
          const existingDocLabel = `${docName?.trim() || affidavitNameForSlot(docIdx)}.pdf`;
          const fileLabel = ck && filesRef.current[ck] ? filesRef.current[ck].name : mediaId ? existingDocLabel : '';
          const inputId = `vpof-${docIdx}`;
          return (
            <div key={field.id} className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
              <input type="hidden" {...register(`documents.${docIdx}.clientKey`)} />
              <div className="flex flex-col gap-1">
                <Label className="text-sm font-medium">Document Name</Label>
                <Input
                  className="bg-white"
                  placeholder={affidavitNameForSlot(docIdx)}
                  {...register(`documents.${docIdx}.name`)}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-sm font-medium">Upload</Label>
                <label
                  htmlFor={inputId}
                  className="flex flex-row items-center gap-2 rounded-md bg-white border border-dashed border-[#92A1B3] px-4 h-12 cursor-pointer"
                >
                  <Upload className="h-5 w-5 text-primary shrink-0" strokeWidth={2} />
                  <span
                    className={`text-sm flex-1 truncate ${fileLabel ? 'text-primary' : 'text-placeholder'}`}
                    title={fileLabel || 'Click to upload a file'}
                  >
                    {fileLabel || 'Click to upload a file'}
                  </span>
                  {fileLabel ? (
                    <button
                      type="button"
                      aria-label="Remove file"
                      className="text-primary hover:text-[#12375C]"
                      onClick={(e) => {
                        e.preventDefault();
                        const k = docClientKey(docIdx);
                        if (k) delete filesRef.current[k];
                        const el = document.getElementById(inputId) as HTMLInputElement | null;
                        if (el) el.value = '';
                        if (fields.length > 1) {
                          remove(docIdx);
                        } else {
                          setValue(`documents.${docIdx}.mediaId`, '', { shouldDirty: true });
                          forceRerender((n) => n + 1);
                        }
                      }}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                </label>
                <input
                  id={inputId}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    const k = docClientKey(docIdx);
                    if (!k) {
                      e.target.value = '';
                      return;
                    }
                    if (!file) {
                      delete filesRef.current[k];
                      e.target.value = '';
                      forceRerender((n) => n + 1);
                      return;
                    }
                    if (file.type !== 'application/pdf') {
                      delete filesRef.current[k];
                      toast({ description: 'Proof of service must be a PDF.', variant: 'destructive' });
                      e.target.value = '';
                      forceRerender((n) => n + 1);
                      return;
                    }
                    if (file.size > MAX_PDF_BYTES) {
                      delete filesRef.current[k];
                      toast({ description: 'PDF must be 20 MB or smaller.', variant: 'destructive' });
                      e.target.value = '';
                      forceRerender((n) => n + 1);
                      return;
                    }
                    filesRef.current[k] = file;
                    forceRerender((n) => n + 1);
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const ProcessBillingVendorFormPage = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = location.pathname.endsWith('/edit');
  const user = useUserStore((s) => s.user);
  const queryClient = useQueryClient();
  const filesRef = useRef<Record<string, File | undefined>>({});
  const initializedKeyRef = useRef<string | null>(null);

  const woQuery = useGetWorkOrderById(workOrderId, { refetchOnMount: 'always' });
  const billingQuery = useQuery({
    queryKey: ['processBillingVendor', workOrderId],
    queryFn: () => getProcessBillingVendor(workOrderId!),
    enabled: Boolean(workOrderId),
    retry: false,
  });

  const form = useForm<VendorBillingFormValues>({
    defaultValues: {
      vendorName: '',
      vendorContactName: '',
      vendorServerName: '',
      serverId: '',
      vendorInvoiceNumber: '',
      amountDue: '',
      documents: [{ clientKey: crypto.randomUUID(), name: '', mediaId: '' }],
    },
  });

  const flagOn = hasFeature(Feature.PROCESS_BILLING_VENDOR) || woQuery.data?.service?.type === 'PROCESS_SERVICE';

  const { handleSubmit, reset, control, setError } = form;

  useEffect(() => {
    if (!woQuery.isFetching && woQuery.data && !flagOn) {
      navigate('/404', { replace: true });
    }
  }, [woQuery.isFetching, woQuery.data, flagOn, navigate]);

  useEffect(() => {
    if (!woQuery.data || !isProcessBillingVendorEligible(woQuery.data)) return;
    const existingBatchId = billingQuery.data?.processBillingBatchId ?? null;
    const hasVendorEntry = Boolean(billingQuery.data?.entry);
    const initKey = `${workOrderId}::${isEdit ? 'edit' : 'create'}`;

    if (!isEdit) {
      if (existingBatchId && hasVendorEntry) {
        navigate(`/process/workorder/${workOrderId}/process-billing-vendor`, { replace: true });
        return;
      }
      if (initializedKeyRef.current === initKey) return;
      initializedKeyRef.current = initKey;
      filesRef.current = {};
      reset(buildCreateDefaults(woQuery.data));
      return;
    }
    if (billingQuery.isError) {
      navigate(`/process/workorder/${workOrderId}/process-billing-vendor/create`, { replace: true });
      return;
    }
    if (!billingQuery.data) return;
    if (!hasVendorEntry) {
      navigate(`/process/workorder/${workOrderId}/process-billing-vendor/create`, { replace: true });
      return;
    }
    if (initializedKeyRef.current === initKey) return;
    initializedKeyRef.current = initKey;
    filesRef.current = {};
    reset(buildEditDefaults(woQuery.data, billingQuery.data));
  }, [woQuery.data, billingQuery.data, billingQuery.isError, isEdit, reset, navigate, workOrderId]);

  const createMutation = useMutation({
    mutationFn: async (body: ProcessBillingVendorPostRequest) => {
      if (!workOrderId) throw new Error('Missing work order');
      return postProcessBillingVendor(workOrderId, body);
    },
    onError: (err: unknown) => {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
          : undefined;
      toast({ description: msg ?? 'Save failed.', variant: 'destructive' });
    },
  });

  const patchMutation = useMutation({
    mutationFn: async (body: ProcessBillingVendorPatchRequest) => {
      if (!workOrderId) throw new Error('Missing work order');
      return patchProcessBillingVendor(workOrderId, body);
    },
    onError: (err: unknown) => {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
          : undefined;
      toast({ description: msg ?? 'Update failed.', variant: 'destructive' });
    },
  });

  const confirmMutation = useMutation({
    mutationFn: async () => {
      if (!workOrderId) throw new Error('Missing work order');
      return confirmProcessBillingVendor(workOrderId);
    },
    onError: (err: unknown) => {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
          : undefined;
      toast({ description: msg ?? 'Confirm failed.', variant: 'destructive' });
    },
  });

  const isAdminOrSupervisor = isAdminOrSupervisorUser(user);
  const blockedFromEdit = isEdit && !isAdminOrSupervisor;
  const canSubmit =
    !blockedFromEdit &&
    (isAdminOrSupervisor || canAccessProcessBilling(user));

  const buildPayload = async (
    values: VendorBillingFormValues,
    mode: 'create' | 'edit'
  ): Promise<ProcessBillingVendorPostRequest | ProcessBillingVendorPatchRequest | null> => {
    let amountDue: number;
    try {
      amountDue = parseMoney('Amount Due', values.amountDue);
    } catch (e) {
      setError('amountDue', {
        type: 'manual',
        message: e instanceof Error ? e.message : 'Invalid Amount Due',
      });
      return null;
    }

    const invoice = trimToString(values.vendorInvoiceNumber);
    if (!invoice) {
      setError('vendorInvoiceNumber', { type: 'manual', message: 'Vendor Invoice Number is required' });
      return null;
    }
    if (!VENDOR_INVOICE_PATTERN.test(invoice)) {
      setError('vendorInvoiceNumber', {
        type: 'manual',
        message: 'Vendor Invoice Number must be alphanumeric',
      });
      return null;
    }

    const serverIdRaw = trimToString(values.serverId);
    if (serverIdRaw && !VENDOR_SERVER_ID_PATTERN.test(serverIdRaw)) {
      setError('serverId', {
        type: 'manual',
        message: 'Server ID must be alphanumeric and may contain hyphens',
      });
      return null;
    }

    if (!values.documents?.length) {
      toast({ description: 'Proof of Service is required.', variant: 'destructive' });
      return null;
    }

    type ProofItem = {
      mediaId: string;
      name: string;
      sortOrder?: number;
      processBillingVendorDocumentId?: string;
    };
    const proof: ProofItem[] = [];
    for (const [di, d] of values.documents.entries()) {
      let mediaId = d.mediaId?.trim() ?? '';
      const file = filesRef.current[d.clientKey];
      if (!mediaId && file) {
        try {
          const uploaded = await uploadMedia(file, 'PROCESS_BILLING');
          if (!uploaded?.mediaId) {
            toast({ description: 'Failed to upload PDF. Please try again.', variant: 'destructive' });
            return null;
          }
          mediaId = uploaded.mediaId;
        } catch (error) {
          toast({
            description: `Failed to upload PDF. Please try again.${error instanceof Error ? ' ' + error.message : ''}`,
            variant: 'destructive',
          });
          return null;
        }
      }
      if (!mediaId) {
        toast({ description: 'Proof of Service is required.', variant: 'destructive' });
        return null;
      }
      const rawName = d.name?.trim() ?? '';
      const name = rawName || affidavitNameForSlot(di);
      if (mode === 'edit' && d.processBillingVendorDocumentId) {
        proof.push({
          processBillingVendorDocumentId: d.processBillingVendorDocumentId,
          mediaId,
          name,
          sortOrder: di,
        });
      } else if (mode === 'edit') {
        proof.push({ mediaId, name, sortOrder: di });
      } else {
        proof.push({ mediaId, name });
      }
    }

    return {
      vendorServerName: trimToString(values.vendorServerName) || null,
      serverId: serverIdRaw || null,
      vendorInvoiceNumber: invoice,
      amountDue,
      proofOfService: proof,
    };
  };

  const onSubmit = (confirmAfter: boolean) => async (values: VendorBillingFormValues) => {
    if (blockedFromEdit) return;
    try {
      const payload = await buildPayload(values, isEdit ? 'edit' : 'create');
      if (!payload) return;

      if (isEdit) {
        await patchMutation.mutateAsync(payload as ProcessBillingVendorPatchRequest);
      } else {
        await createMutation.mutateAsync(payload as ProcessBillingVendorPostRequest);
      }

      if (confirmAfter) {
        await confirmMutation.mutateAsync();
        toast({ description: 'Vendor billing saved and confirmed.' });
      } else {
        toast({ description: isEdit ? 'Vendor billing updated.' : 'Vendor billing saved.' });
      }

      await queryClient.invalidateQueries({ queryKey: ['processBillingVendor', workOrderId] });
      navigate(`/process/workorder/${workOrderId}/process-billing-vendor`);
    } catch {
      // mutation onError already toasted
    }
  };

  const wo = woQuery.data;
  const loading = (woQuery.isFetching && !woQuery.data) || (billingQuery.isFetching && !billingQuery.data);

  if (blockedFromEdit) {
    return (
      <div className="w-full max-w-xl rounded-lg border border-destructive/40 p-8 text-center space-y-4">
        <h1 className="text-lg font-semibold text-destructive">Permission denied</h1>
        <p className="text-sm text-placeholder">
          Only Supervisors and Administrators may edit existing vendor billing entries.
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate(`/process/workorder/${workOrderId}/process-billing-vendor`)}
        >
          Back to view
        </Button>
      </div>
    );
  }

  const woNumber = wo?.number ?? '';
  const titleSuffix = isEdit ? 'Edit Vendor Billing Entry' : 'Add Vendor Billing Entry';
  const eligible = wo ? isProcessBillingVendorEligible(wo) : false;
  const isPending = createMutation.isPending || patchMutation.isPending || confirmMutation.isPending;

  return (
    <Loader isLoading={loading}>
      <FormProvider {...form}>
        <form className="w-full h-full min-w-[170px] flex flex-col gap-6">
          <h1 className="text-2xl font-semibold text-primary tracking-tight">{`W/O: ${woNumber} -  ${titleSuffix}`}</h1>

          {!wo || !eligible ? (
            <div className="text-sm text-destructive">
              This work order is not eligible for vendor billing. Vendor billing requires Process Service with a vendor
              and contact assigned (National, International, Deputization, UIDDA, or Due Diligence).
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6 mt-2">
              <div className="flex flex-col gap-1">
                <Label className="flex items-center gap-1 text-sm font-medium">
                  Vendor Name<span className="text-error align-top">*</span>
                </Label>
                <Input
                  value={form.watch('vendorName')}
                  readOnly
                  disabled
                  className="bg-[#F5F5F5] border-bordercolor text-placeholder disabled:opacity-100 disabled:cursor-default"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="flex items-center gap-1 text-sm font-medium">
                  Vendor Contact Name<span className="text-error align-top">*</span>
                </Label>
                <Input
                  value={form.watch('vendorContactName') || '—'}
                  readOnly
                  disabled
                  className="bg-[#F5F5F5] border-bordercolor text-placeholder disabled:opacity-100 disabled:cursor-default"
                />
              </div>
              <FormFieldComponent
                name="vendorServerName"
                control={control}
                label="Vendor Server Name"
                placeholder="Enter Vendor Server Name"
              />
              <FormFieldComponent name="serverId" control={control} label="Server ID" placeholder="Enter Server ID" />
              <FormFieldComponent
                name="vendorInvoiceNumber"
                control={control}
                label="Vendor Invoice Number"
                placeholder="Enter Vendor Invoice Number"
                required
              />
              <FormFieldComponent
                name="amountDue"
                control={control}
                label="Amount Due"
                placeholder="Enter Amount Due"
                type="number"
                required
                allowNegativeNumbers={false}
                maxDecimalPlaces={2}
                step="0.01"
              />
              <div className="md:col-span-2">
                <VendorDocumentsSection filesRef={filesRef} />
              </div>
            </div>
          )}

          <div className="flex flex-row gap-3 justify-end mt-6">
            <Button
              type="button"
              variant="outline"
              className="w-[125px] h-[48px] border-primary text-primary hover:bg-[#EDF5FF]"
              onClick={() => navigate(-1)}
              disabled={isPending}
            >
              Back
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-[48px] px-6 border-primary text-primary hover:bg-[#EDF5FF]"
              disabled={!canSubmit || isPending || !eligible}
              onClick={handleSubmit(onSubmit(false))}
            >
              Save
            </Button>
            <Button
              type="button"
              className="h-[48px] px-6 hover:bg-[#12375C]"
              disabled={!canSubmit || isPending || !eligible}
              onClick={handleSubmit(onSubmit(true))}
            >
              Save and Confirm
            </Button>
          </div>
        </form>
      </FormProvider>
    </Loader>
  );
};
